/**
 * The confinement an impersonation grant request actually carries.
 *
 * Workspace, then project, then organisation. Each rung narrows what the grant
 * reaches, and each is meaningful ONLY alongside the one above it: the server
 * refuses an organisation named without a project rather than guessing which
 * project it is in, exactly as it refuses a project that does not belong to the
 * named workspace — refused, never widened, so a wrong pairing fails loudly
 * instead of quietly conferring more than was asked for.
 *
 * ── Why it lives here, in the shared package ────────────────────────────────
 *
 * There are TWO ways a grant is asked for, and the same ladder governs both:
 *
 *   - BREAK-GLASS, on the staff console's Impersonation page, where a user
 *     names all three rungs by hand because nobody at the workspace is in the
 *     loop (apps/staff/src/routes/impersonation/index.tsx).
 *   - THE APPROVED REQUEST, the dialog the session provider raises on entry,
 *     where the rungs are the scope the user is already standing in
 *     (./impersonation-request.tsx).
 *
 * Two copies of this rule is the failure worth designing against: they would
 * not disagree on the day they were written, they would disagree on the day one
 * of them was edited, and the half that drifted would compose a request the
 * server refuses — spending the refusal on whichever path was not the one
 * someone was looking at. One function, imported by both.
 *
 * The pickers on the break-glass form enforce the same order by going dormant,
 * and clear the rungs below them when they change. This exists anyway, as ONE
 * place the rule is written, because the pickers enforce it by construction and
 * construction is exactly what a later edit changes. It is also the only form
 * of the rule a test can put a finger on: the alternative is asserting against
 * a rendered dialog, which measures the pickers rather than the request.
 *
 * IT ALSO DECIDES WHAT THE SCREEN SAYS, on both paths, and that is deliberate
 * rather than incidental. The break-glass slider names what it is about to
 * enter and the request dialog names what it is about to ask for; both read
 * their sentence off the value returned here, so a surface cannot describe a
 * reach different from the one being requested.
 *
 * EMPTY MEANS ABSENT. A field is `undefined` rather than "", so an unconfined
 * request sends no field at all and reads on the wire as the workspace-wide ask
 * it is — "" would be a named-but-empty project, which is a different claim.
 */
export interface GrantConfinement {
  /** Omitted for a workspace-wide grant. */
  projectId?: string;
  /** Omitted for a project-wide grant. Never present without `projectId`. */
  orgId?: string;
}

export function grantConfinement({
  projectId,
  orgId,
}: {
  /** "" for workspace-wide. */
  projectId: string;
  /** "" for project-wide. */
  orgId: string;
}): GrantConfinement {
  if (!projectId) return {};
  return { projectId, ...(orgId ? { orgId } : {}) };
}
